import {
  Box,
  Drawer,
  FormControl,
  IconButton,
  Typography,
  useMediaQuery,
  FormLabel,
  Checkbox,
  Stack,
  TextField,
  InputAdornment,
} from "@mui/material";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import React, { useEffect, useState } from "react";
import {
  FiltersBills,
  InputsAmountData,
  ServiceEnvirormentData,
  ServiceEnvirormentDataId,
  TotalEnvirormentState,
} from "../../../core/models/bills/bills.model";
import "dayjs/locale/es";
import "./BillsFilters.scss";
import { useTranslation } from "react-i18next";
import DatePickersComponent from "../../UI/DatePickersComponent/DatePickersComponent";
import FilterCifCups from "../../FilterCifCups/FilterCifCups";
import { initialFiltersBills } from "../../../utils/components/bills/bills.util";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";
import { FiltersCifCups } from "../../../core/models/filtersCifCups/filtersCifCups.model";
import theme from "../../../muiTheme";
import { formatNumber } from "../../../utils/formatters/formatters.util";
import { useDatePickerValidation } from "../../../utils/components/datePickers/datePickers.utils";

export interface BillsFiltersProps {
  isFiltersOpen: boolean;
  setIsFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filtersBills: FiltersBills;
  setFiltersBills: (filtersBills: FiltersBills) => void;
  ownerDataCups: OwnerDataCups[];
  totalEnvirormentState: TotalEnvirormentState;
}

const BillsFilters = ({
  isFiltersOpen,
  setIsFiltersOpen,
  filtersBills,
  setFiltersBills,
  ownerDataCups,
  totalEnvirormentState,
}: BillsFiltersProps) => {
  const { t } = useTranslation();
  const isMobileViewport = useMediaQuery("(max-width: 500px )");
  const [drawerFiltersBills, setDrawerFiltersBills] =
    useState<FiltersBills>(filtersBills);
  const [drawerFiltersCifCups, setDrawerFiltersCifCups] =
    useState<FiltersCifCups>(drawerFiltersBills);
  const [firstInputChanged, setFirstInputChanged] = useState<string>("");
  const { dateFrom, dateTo, emissionDateFrom, emissionDateTo } =
    drawerFiltersBills;
  const {
    error: errorBillingDate,
    setError: setErrorBillingDate,
    isValidDate: isValidBillingDate,
  } = useDatePickerValidation();
  const {
    error: errorEmissionDate,
    setError: setErrorEmissionDate,
    isValidDate: isValidEmissionDate,
  } = useDatePickerValidation();

  useEffect(() => {
    if (isFiltersOpen) {
      setDrawerFiltersBills(filtersBills);
    }

    setDrawerFiltersBills((prevDrawerFiltersBills) => ({
      ...prevDrawerFiltersBills,
      ...drawerFiltersCifCups,
    }));
  }, [drawerFiltersCifCups, filtersBills, isFiltersOpen]);

  const serviceEnvirormentData: ServiceEnvirormentData[] = [
    {
      id: "environmentCode",
      title: "Servicios",
      values: [
        {
          id: "light",
          label: "Luz",
          value: "E1",
          quantity: totalEnvirormentState.totalCountLight,
        },
        {
          id: "gas",
          label: "Gas",
          value: "E2",
          quantity: totalEnvirormentState.totalCountGas,
        },
      ],
    },
    {
      id: "billStateCode",
      title: "Estado",
      values: [
        {
          id: "paid",
          label: "Pagadas",
          value: "Pagado",
          quantity: totalEnvirormentState.totalCountPaid,
        },
        {
          id: "pending",
          label: "Pendientes",
          value: "Pendiente",
          quantity: totalEnvirormentState.totalCountPending,
        },
        {
          id: "noPaid",
          label: "No pagadas",
          value: "No pagado",
          quantity: `${totalEnvirormentState.totalCountNoPaid}`,
        },
      ],
    },
  ];

  const inputsAmountData: InputsAmountData[] = [
    {
      id: "amountMin",
      label: "Importe mínimo",
    },
    {
      id: "amountMax",
      label: "Importe máximo",
    },
  ];

  const handleCheckboxChange = (
    id: ServiceEnvirormentDataId,
    value: string,
    checked: boolean
  ) => {
    if (!value) return;

    setDrawerFiltersBills((prevDrawerFiltersBills) => {
      if (id === "environmentCode") {
        return {
          ...prevDrawerFiltersBills,
          [id]: checked ? value : "",
        };
      }

      let currentValues: string[] = [];

      const prevValue = prevDrawerFiltersBills[id] as unknown;

      if (Array.isArray(prevValue)) {
        currentValues = prevValue as string[];
      } else if (typeof prevValue === "string") {
        currentValues = [prevValue];
      }

      if (checked) {
        return {
          ...prevDrawerFiltersBills,
          [id]: [...currentValues, value].filter(Boolean),
        };
      } else {
        return {
          ...prevDrawerFiltersBills,
          [id]: currentValues.filter((item: string) => item !== value),
        };
      }
    });
  };

  return (
    <Drawer
      open={isFiltersOpen}
      PaperProps={
        isMobileViewport
          ? { sx: { width: "100%" } }
          : { sx: { width: "31rem" } }
      }
    >
      <Box
        component="div"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding={{ xs: "1.5rem 1rem", md: "1.5rem 2.25rem" }}
        className="bills-filter-box"
      >
        <Typography component="h3" variant="h3">
          {t("Tablas.Filtros")}
        </Typography>
        <IconButton disableRipple onClick={() => setIsFiltersOpen(false)}>
          <span className="t-icon-close brand-red" />
        </IconButton>
      </Box>
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"2rem"}
        sx={{
          padding: { md: "2rem 2.25rem 0 2.25rem", xs: "2rem 1rem 0 1rem" },
        }}
      >
        {serviceEnvirormentData.map(({ id: idData, title, values }) => (
          <FormControl key={idData}>
            <FormLabel style={{ paddingBottom: ".85rem" }}>
              <Typography fontWeight={600}>{t(`Tablas.${title}`)}</Typography>
            </FormLabel>
            {values.map(({ id, label, value, quantity }, index) => (
              <Box
                key={id}
                component={"div"}
                display={"flex"}
                fontSize="24px"
                paddingBottom={values.length - 1 !== index ? "9px" : ""}
              >
                <Checkbox
                  icon={<span className="t-icon-checkbox-default"></span>}
                  checked={
                    idData === "environmentCode"
                      ? drawerFiltersBills[idData] === value
                      : Array.isArray(drawerFiltersBills[idData]) &&
                        drawerFiltersBills[idData].includes(value)
                  }
                  checkedIcon={
                    <span className="t-icon t-icon-checkbox-checked"></span>
                  }
                  onChange={({ target: { checked } }) =>
                    handleCheckboxChange(idData, value, checked)
                  }
                  disabled={Number(quantity) === 0}
                />
                <Typography
                  component={"p"}
                  variant="subtitle2"
                  paddingLeft="0.5rem"
                  color={theme.palette.grey[700]}
                  className={Number(quantity) === 0 ? "text-disabled" : ""}
                >
                  {`${t(`Tablas.${label}`)} (${formatNumber(+quantity)})`}
                </Typography>
              </Box>
            ))}
          </FormControl>
        ))}
      </Box>
      <FilterCifCups
        isFiltersOpen={isFiltersOpen}
        ownerDataCups={ownerDataCups}
        filters={filtersBills}
        drawerFilters={drawerFiltersCifCups}
        setDrawerFilters={setDrawerFiltersCifCups}
        firstInputChanged={firstInputChanged}
        setFirstInputChanged={setFirstInputChanged}
      />
      <form className="bills-filters__form">
        <DatePickersComponent
          error={errorBillingDate}
          setError={setErrorBillingDate}
          billsSearchBar
          filters={{ dateFrom, dateTo }}
          setFilters={(id, value) =>
            setDrawerFiltersBills((prevDrawerFiltersBills) => ({
              ...prevDrawerFiltersBills,
              [id]: value,
            }))
          }
        />
        <DatePickersComponent
          error={errorEmissionDate}
          setError={setErrorEmissionDate}
          billsSearchBar
          label="Fecha de emisión"
          filters={{ dateFrom: emissionDateFrom, dateTo: emissionDateTo }}
          setFilters={(id, value) => {
            const idFormatted =
              id === "dateFrom" ? "emissionDateFrom" : "emissionDateTo";
            setDrawerFiltersBills((prevDrawerFiltersBills) => ({
              ...prevDrawerFiltersBills,
              [idFormatted]: value,
            }));
          }}
        />
        <FormControl>
          <FormLabel style={{ paddingBottom: ".85rem" }}>
            <Typography fontWeight={600}>
              {t(`Tablas.Número de factura`)}
            </Typography>
          </FormLabel>
          <TextField
            sx={{ width: "100%" }}
            autoComplete="off"
            inputProps={{
              style: { boxShadow: "none", height: "2.15rem" },
            }}
            InputProps={{
              fullWidth: true,
            }}
            value={
              drawerFiltersBills.invoiceNumberSerie
                ? drawerFiltersBills.invoiceNumberSerie
                : ""
            }
            onChange={({ target: { value } }) =>
              setDrawerFiltersBills((prevDrawerFiltersBills) => ({
                ...prevDrawerFiltersBills,
                invoiceNumberSerie: value,
              }))
            }
            placeholder={t(`Tablas.Introduce el número de factura`)}
          />
        </FormControl>
        <Box
          sx={{
            paddingBottom: { md: "0", xs: "4.5rem" },
          }}
        >
          <FormLabel>
            <Typography fontWeight={600} style={{ paddingBottom: ".85rem" }}>
              {t(`Tablas.Importe de factura`)}
            </Typography>
          </FormLabel>
          <FormControl
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: ".5rem",
            }}
          >
            {inputsAmountData.map(({ id, label }, index) => (
              <React.Fragment key={id}>
                <TextField
                  inputProps={{
                    style: { boxShadow: "none", height: "2.15rem" },
                  }}
                  InputProps={{
                    autoComplete: "off",
                    fullWidth: true,
                    type: "number",
                    endAdornment: (
                      <InputAdornment position="end">
                        <span style={{ color: "#ED0000" }}>€</span>
                      </InputAdornment>
                    ),
                  }}
                  value={drawerFiltersBills[id] ? drawerFiltersBills[id] : ""}
                  onChange={({ target: { value } }) =>
                    setDrawerFiltersBills((prevDrawerFiltersBills) => ({
                      ...prevDrawerFiltersBills,
                      [id]: +value,
                    }))
                  }
                  placeholder={t(`Tablas.${label}`)}
                />
                {!index && (
                  <Typography component="p" variant="body1">
                    {t("Tablas.Al")}
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </FormControl>
        </Box>
      </form>

      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        component="div"
        justifyContent="space-between"
        alignItems="center"
        padding={isMobileViewport ? "0.75rem 1rem" : "0.75rem 2.5rem"}
        position="fixed"
        width={isMobileViewport ? "100%" : "31rem"}
        boxSizing="border-box"
        bottom="0"
        gap={"1rem"}
        className="bottom-bills-filters"
        bgcolor={theme.palette.background.paper}
      >
        <ButtonElement
          onClick={() => {
            setDrawerFiltersBills(initialFiltersBills);
            setDrawerFiltersCifCups(initialFiltersBills);
          }}
          styles={{ flexGrow: isMobileViewport ? "1" : "auto" }}
          link
        >
          {t("Tablas.Borrar filtros")}
        </ButtonElement>
        <ButtonElement
          disabled={!isValidBillingDate || !isValidEmissionDate}
          onClick={() => {
            setFiltersBills({
              ...drawerFiltersBills,
              dateFrom: dateFrom && dateTo ? dateFrom : null,
              dateTo: dateFrom && dateTo ? dateTo : null,
            });
            setIsFiltersOpen(false);
          }}
        >
          {t("Tablas.Aplicar filtros")}
        </ButtonElement>
      </Stack>
    </Drawer>
  );
};

export default BillsFilters;
